import type {
    Experience,
    ExperienceVersion,
    UpdateEngageV4Input,
    TrafficModeType
} from '@models/experience/experience';
import numberUtils from '@utils/number.utils';
import { cloneDeep, isEqual } from 'lodash-es';
import { schedueDefaultMeta } from '@experience/config/setting.config';
import dayjs from 'dayjs';
import i18n from '@assets/i18n';

// experience setting页面的一些方法

/**
 * 统一处理给rate / 100
 * @param versions
 * @returns
 */
export function unFormatVersionsRate(versions: ExperienceVersion[]) {
    if (!versions || versions.length <= 0) {
        return versions;
    }
    versions.map((v: ExperienceVersion) => {
        v.rate = numberUtils.strip(v.rate / 100);
    });
    return versions;
}

/**
 * 统一处理version rate * 100
 * @param versions
 * @returns
 */
export function formatVersionsRate(versions: ExperienceVersion[]) {
    if (!versions || versions.length <= 0) {
        return versions;
    }
    versions.map((v: ExperienceVersion) => {
        v.rate = numberUtils.strip(v.rate * 100);
    });
    return versions;
}

/**
 * 初始化experience创建参数
 * @param param0
 * @returns
 */
export function initExperience({
    timezone,
    commonUsergroupId
}: {
    timezone?: string;
    commonUsergroupId: string;
}) {
    return {
        name: '',
        engageType: '',
        isABTest: false,
        conditions: [
            {
                when: []
            }
        ],
        schedule: [
            {
                timezone: {
                    profile: timezone,
                    useVisitor: false
                },
                versionData: [],
                versionMeta: cloneDeep(schedueDefaultMeta)
            }
        ],
        usergroups: [
            {
                type: 'USER_GROUP',
                value: [commonUsergroupId]
            }
        ],
        trigger: {
            start: [],
            stop: [],
            frequency: {
                type: 'default'
            }
        }
    };
}

/**
 * 判断experience是否有inline
 * @param experienceInfo
 * @returns
 */
export const experienceHasInline = (experienceInfo: Experience) => {
    return (experienceInfo?.versions || []).some(v => {
        const { controlGroup, terminals } = v || {};
        return (
            !controlGroup &&
            (terminals || []).some(t => {
                const { status, popup, stickybar } = t || {};
                const hasError =
                    status === 1 &&
                    [...(popup || []), ...(stickybar || [])].some(layout => layout.popupHasError);
                return t.status === 1 && !hasError && (t?.inline || []).length > 0;
            })
        );
    });
};

/**
 * 判断experience是否设置了schedule
 * @param experienceInfo
 * @returns
 */
export const experienceHasSchedule = (experienceInfo: Experience) => {
    return (
        experienceInfo.schedule?.[0].versionMeta.start === '2' &&
        experienceInfo.status !== 'RUNNING'
    );
};

/**
 * experience的计算属性  usergroup换算
 * @param experienceInfo
 * @returns
 */
export function formatExperienceInfo(experienceInfo: Experience) {
    if (!experienceInfo) {
        return null;
    }
    const experience = cloneDeep(experienceInfo);
    const { usergroups } = experience;
    // if (versions && versions.length > 0) {
    //     versions.map(item => {
    //         item.rate = numberUtils.strip(item.rate * 100);
    //         return item;
    //     });
    // }
    if (usergroups?.length > 0) {
        const result: any = [];
        usergroups.map(item => {
            if (item.type === 'CONDITION') {
                result.push(item.value);
            } else if (item.type === 'USER_GROUP') {
                result.push({
                    type: 'userGroup',
                    value: item.value?.map((v: { code?: any; id: any }) => {
                        return v.id;
                    })
                });
            }
        });
        experience['usergroups'] = result;
    }
    return experience;
}

/**
 * 判断是否有内容更新
 * @param params
 * @param experienceInfo
 * @returns
 */
export function judgeHasSync(params: UpdateEngageV4Input, experienceInfo: Experience) {
    let hasUpdate = false;
    // goals, experience name,  version name 更改不算在内
    if (!params || !experienceInfo) return false;
    Object.keys(params).map((key: any) => {
        if (key === 'versions') {
            const newVersions = params.versions;
            const originVersions = experienceInfo.versions;
            if (newVersions?.length !== originVersions?.length) {
                hasUpdate = true;
            } else {
                newVersions?.map((v, vi) => {
                    const originVersion = originVersions?.find(o => o.id === v.id);
                    originVersion &&
                        Object.keys(v).map(vk => {
                            if (!isEqual(v[vk], originVersion[vk]) && vk !== 'name') {
                                hasUpdate = true;
                            }
                        });
                });
            }
        } else if (
            !isEqual(params[key], experienceInfo[key]) &&
            !['goals', 'name', 'calculateDays', 'calculateConfidence'].includes(key)
        ) {
            hasUpdate = true;
        }
    });
    return hasUpdate;
}

/**
 * 平均分配版本rate
 * @param versions
 * @param trafficMode
 * @returns
 */
export function averageVersionsRateFn(
    versions: ExperienceVersion[] | undefined,
    trafficMode?: TrafficModeType
): ExperienceVersion[] {
    if (!versions || !versions.length) return;
    if (['AVERAGE', 'INTELLIGENT'].includes(trafficMode || 'AVERAGE')) {
        const clonedVersions = formatVersionsRate(cloneDeep(versions));
        const index: number = clonedVersions.length;
        // 平均分配模式计算逻辑：流量按版本平均分配。如果不能整除版本，从最后版本开始分配余数
        // 示例：7版本：14% 14% 14% 14% 14% 15% 15%
        let remainder = 100 % index;
        const divideIntRate = numberUtils.strip(parseInt(100 / index + ''));
        for (let i = index - 1; i >= 0; i--) {
            clonedVersions[i].rate = remainder > 0 ? divideIntRate + 1 : divideIntRate;
            remainder--;
        }
        return unFormatVersionsRate(clonedVersions);
    }
    return versions;
}

/**
 * 自定义trafficMode 按比例分配
 * @param versions
 * @param pauseRate
 * @returns
 */
export function divideVersionRateByproportion(versions: ExperienceVersion[], pauseRate: number) {
    if (!versions || pauseRate === undefined) return versions;
    const cloneVersions = formatVersionsRate(cloneDeep(versions));
    const valideCloneVersions = cloneVersions?.filter(c => c.rate);
    const pauseRate100 = pauseRate * 100;
    let len: number = valideCloneVersions.length;
    let remainNormalRate = 0;
    valideCloneVersions.map((v, index) => {
        if (index !== len - 1) {
            v.rate = numberUtils.strip(
                v.rate + Math.floor((v.rate / (100 - pauseRate100)) * pauseRate100)
            );
            remainNormalRate += v.rate;
        } else {
            v.rate = numberUtils.strip(100 - remainNormalRate);
        }
        return v;
    });
    return unFormatVersionsRate(cloneVersions);
}

/**
 * 处理shchedule 历史数据 startDate => startDateStr  stopDate => stopDateStr
 * @param versionMeta
 * @returns
 */
export function initScheduleData(versionMeta: any) {
    if (!versionMeta) return null;
    const { startDate, stopDate, startDateStr, stopDateStr } = versionMeta;
    if (!startDateStr || !stopDateStr) {
        versionMeta.startDateStr = dayjs(startDate).format('YYYY-MM-DD HH:mm:ss');
        versionMeta.stopDateStr = dayjs(stopDate).format('YYYY-MM-DD HH:mm:ss');
    }
    return versionMeta;
}

/**
 *  处理usergroup历史数据 visitPage entryPage 没有subType值得 默认给page
 * @param usergroups
 * @returns
 */
export function initUsergroupData(usergroups: any) {
    if (!usergroups) return null;
    const cloneUsergroups = cloneDeep(usergroups);
    if (cloneUsergroups?.length > 0) {
        cloneUsergroups.map((item: any) => {
            if (['visitPage', 'entryPage'].includes(item.type) && !item.subType) {
                item.subType = 'page';
            }
        });
    }
    return cloneUsergroups;
}

export function getVersionNameNum(versions: ExperienceVersion[]) {
    const reg = /(\d+)$/;
    let nameNum = 0;
    versions
        .filter(v => !v.controlGroup)
        .forEach(v => {
            const count = reg.exec(v.name);
            count && (nameNum = Math.max(parseInt(count), nameNum));
        });
    return nameNum;
}

/**
 * 获得自增1的version名字
 */
export function getNewVersionName(version?: ExperienceVersion, nameNum = 0): string {
    const reg = /(\d+)$/;
    const defaultName = i18n.global.t('experience.setting.version_default_name');
    const count = reg.exec(version?.name || '');
    let name: string = defaultName;

    if (version?.controlGroup) {
        name = i18n.global.t('experience.setting.control_group') as string;
    } else if (count) {
        name = `${version?.name.trim().replace(count[0], '')}${nameNum + 1}`;
    }
    return name;
}
